import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { shuffle, sortBy, uniq } from "lodash";
import {
  DashboardSection,
  DashboardWrapper,
  StyleElementContainer,
} from "../styles/dashboardStyles";
import styled from "styled-components";
import StyleElement from "../components/StyleElement";
import { device } from "../utils/breakpoints";
import HomeLayout from "../layouts/HomeLayout";
import { HeaderSubtitle, HeaderTitle } from "../styles/sharedStyles";
import { PROFESSIONS } from "../constants/ProfessionConstants";
import UserCard from "../components/UserCard";
import { Select } from "antd";
import EnterEmailBanner from "../components/Banners/EnterEmailBanner";
import CommunityBanner from "../components/Home/CommunityBanner";
import communityImages from "../data/community-cards.json";
import MetaTags from "../components/MetaTags";
import { colors } from "../utils/theme";
import useIsMobile from "../components/Hooks/useIsMobile"; // Import Spin from antd for loading state
import LoadingPage from "../components/LoadingPage";

const Community = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const lang = i18n.language;
  const users = useSelector((state) => state.firestoreReducer.ordered.users);
  const isLoading = useSelector(
    (state) => state.firestoreReducer.status.requesting.users,
  );

  const [publicUsers, setPublicUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filterItems, setFilterItems] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchResult, setSearchResult] = useState([]);
  const [expandedCardId, setExpandedCardId] = useState(null);

  useEffect(() => {
    if (users && users.length > 0) {
      const publicUsers = users.filter((user) => user.public);
      const shuffledPublicUsers = shuffle(publicUsers);
      setPublicUsers(shuffledPublicUsers);
      setFilteredUsers(shuffledPublicUsers);

      const filterItems = [];
      PROFESSIONS.forEach((profession) => {
        if (publicUsers.some((user) => user.profession === profession.id)) {
          filterItems.push(profession);
        }
      });
      const uniqueFilterItems = uniq(filterItems);
      setFilterItems(uniqueFilterItems);
    }
  }, [users]);

  // useEffect(() => {
  //   // Only process users if they exist and searchResult is empty
  //   if (
  //     filteredUsers &&
  //     filteredUsers.length > 0 &&
  //     searchResult.length === 0
  //   ) {
  //     const nextSortedUsers = sortBy(filteredUsers, [
  //       (item) => {
  //         return item.firstName + " " + item.lastName;
  //       },
  //     ]);
  //     setFilteredUsers(nextSortedUsers);
  //   } else if (searchResult.length > 0) {
  //     const nextSortedUsers = searchResult.map((item) => item.item);
  //     setFilteredUsers(nextSortedUsers);
  //   }
  // }, [filteredUsers, searchResult]);

  useEffect(() => {
    const nextFilteredUsers = publicUsers.filter((user) => {
      if (activeFilter === "all") return true;
      else if (activeFilter === "ARTIST")
        return user.profession === "ARTIST" || user.profession === "MUSICIAN";
      else return user.profession === activeFilter;
    });
    setFilteredUsers(nextFilteredUsers);
  }, [activeFilter, publicUsers]);

  const handleCardExpand = (eventId) => {
    setExpandedCardId(expandedCardId === eventId ? null : eventId);
  };

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <HomeLayout>
        <MetaTags
          title={t("meta.community_title")}
          description={t("meta.community_description")}
          keywords={t("meta.community_keywords")}
          imageUrl="/assets/images/home/OW-community.webp"
          path="/community"
        />
        <DashboardWrapper>
          <DashboardSection>
            <LoadingPage />
          </DashboardSection>
        </DashboardWrapper>
      </HomeLayout>
    );
  }

  // Show message if no users are found
  if (!users || users.length === 0) {
    return (
      <HomeLayout>
        <MetaTags
          title={t("meta.community_title")}
          description={t("meta.community_description")}
          keywords={t("meta.community_keywords")}
          imageUrl="/assets/images/home/OW-community.webp"
          path="/community"
        />
        <DashboardWrapper>
          <DashboardSection>
            <HeaderWrapper>
              <HeaderTitle>{t("the_oostwest_community")}</HeaderTitle>
              <HeaderSubtitle>{t("no_members_found")}</HeaderSubtitle>
            </HeaderWrapper>
          </DashboardSection>
        </DashboardWrapper>
      </HomeLayout>
    );
  }

  return (
    <HomeLayout>
      <MetaTags
        title={t("meta.community_title")}
        description={t("meta.community_description")}
        keywords={t("meta.community_keywords")}
        imageUrl="/assets/images/home/OW-community.webp"
        path="/community"
      />
      <DashboardWrapper>
        <DashboardSection>
          <HeaderWrapper>
            <HeaderTitle>{t("the_oostwest_community")}</HeaderTitle>
            <HeaderSubtitle>{t("our_members")}</HeaderSubtitle>
          </HeaderWrapper>
          {isMobile ? (
            <MobileFilter
              defaultValue={"all"}
              placeHolder={t("filter")}
              onChange={(value) => setActiveFilter(value)}
              options={[
                { label: t("show_all_members"), value: "all" },
                ...filterItems.map((item) => ({
                  label: item[lang],
                  value: item.id,
                })),
              ]}
            />
          ) : (
            <DesktopFilter>
              <li>
                <button
                  onClick={() => setActiveFilter("all")}
                  className={activeFilter === "all" ? "active" : ""}
                >
                  {t("show_all_members")}
                </button>
              </li>
              {filterItems.map((item, i) => (
                <li key={i}>
                  <button
                    onClick={() => setActiveFilter(item.id)}
                    className={activeFilter === item.id ? "active" : ""}
                  >
                    {item[lang]}
                  </button>
                </li>
              ))}
            </DesktopFilter>
          )}
          <UsersGrid>
            {filteredUsers &&
              filteredUsers.length > 0 &&
              filteredUsers.map((user, i) => (
                <UserCard
                  key={i}
                  user={user}
                  isExpanded={expandedCardId === i}
                  onExpand={() => handleCardExpand(i)}
                />
              ))}
          </UsersGrid>
        </DashboardSection>
      </DashboardWrapper>
      <StyleElementContainer>
        <StyleElement
          src={"/assets/images/illustrations/illustratie-14.svg"}
          alt={""}
          bottom={-20}
          right={-13}
        />
      </StyleElementContainer>
      <EnterEmailBanner />
      <CommunityBanner
        title={t("home_community_title")}
        description={t("home_community_description")}
        images={communityImages}
      />
    </HomeLayout>
  );
};

const HeaderWrapper = styled.div``;

const UsersGrid = styled.div`
  z-index: 1;
  display: grid;
  gap: 1rem;
  margin-bottom: 4rem;

  @media only screen and ${device.sm} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media only screen and ${device.md} {
    grid-template-columns: repeat(3, 1fr);
  }

  @media only screen and ${device.lg} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const MobileFilter = styled(Select)`
  @media only screen and ${device.md} {
    max-width: 300px;
  }
`;

const DesktopFilter = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  button {
    border: none;
    background-color: transparent;
    min-width: 32px;
    color: ${colors.secondary};

    &.active {
      font-weight: 600;
    }

    &:hover,
    &:focus-visible {
      text-decoration: underline;
      text-underline-offset: 6px;
      text-decoration-thickness: 2px;
    }
  }
`;

export default Community;
